<template>
    <div
    class="selector-wrapper"
    :class="[{ mobile: getMobileAppState, 'only-dropdown': onlyDropdown, 'with-hide-balances': hideBalancesInMoreOptions }, classes]">
        <!-- Dropdown Account -->
        <dropdown-account
        :class="{ 'only-dropdown': onlyDropdown }"
        :params="{ headerType: 'description-header' }"
        :options="options"
        :selectedOption="selectedOption"
        :hideAmounts="hideAmounts"
        @clicked="emit('clicked', $event)"/>
        <!-- Balances -->
        <div v-if="!onlyDropdown && !hideBalancesInMoreOptions" v-dragscroll="getMobileAppState" class="text-blocks-container inline">
            <!-- Divider -->
            <div v-if="!getMobileAppState" class="divider"/>
            <!-- Available Balance -->
            <text-block
            :params="getAvailableBalanceParams"
            :icon="getAvailableBalanceIcon"
            :colorIconPreset="getAvailableBalanceIconColor"
            :applyEllipsis="!getMobileAppState"/>
            <!-- Account Balance -->
            <text-block
            v-if="!onlyAvailableBalance"
            :params="getAccountBalanceParams"
            :icon="getAccountBalanceIcon"
            :colorIconPreset="getAccountBalanceIconColor"
            :applyEllipsis="!getMobileAppState"/>
        </div>

        <div v-if="hideBalancesInMoreOptions" class="web-low-resolution-balances-wrapper" v-click-outside="() => { showBalancesFakeDrop = false }">
            <!-- Divider -->
            <div class="divider"/>
            <!-- Button Tertiary -->
            <button-tertiary icon="info" buttonSize="small" @clicked="showBalancesFakeDrop = true"/>
            <!-- Balances Wrapper -->
            <div v-if="showBalancesFakeDrop" class="text-blocks-container">
                <!-- Available Balance -->
                <text-block
                :params="getAvailableBalanceParams"
                :icon="getAvailableBalanceIcon"
                :colorIconPreset="getAvailableBalanceIconColor"
                :applyEllipsis="!getMobileAppState"/>
                <!-- Account Balance -->
                <text-block
                v-if="!onlyAvailableBalance"
                :params="getAccountBalanceParams"
                :icon="getAccountBalanceIcon"
                :colorIconPreset="getAccountBalanceIconColor"
                :applyEllipsis="!getMobileAppState"/>
            </div>
        </div>
        <!-- Gradient for overflow @mobile -->
        <div v-if="getMobileAppState && !onlyDropdown && !hideBalancesInMoreOptions" class="mobile-gradient"/>
    </div>
</template>

<script setup>
import DropdownAccount from '@/components/generic/dropdowns/accounts/Account'
import TextBlock from '@/components/generic/textBlocks/Generic'
import ButtonTertiary from '@/components/generic/buttons/Tertiary'

/* Vue */
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'

/* Store */
import { mapGetters } from '@/store/helpers/mappers'

/* Aux */
import { amountFormat } from '@/assets/scripts/amounts'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* mapGetters */
const { getMobileAppState } = mapGetters()

/* Emmitters */
const emit = defineEmits(['clicked'])

/* props received
@classes {String} string with multiple classes to apply
@onlyDropdown {Boolean} - prop to only display our dropdown
@onlyAvailableBalance {Boolean} - prop to only show info about available balance
@selectedOption { String, Number }
@options { Array } each item is an Object with:
	- id { String, Number }
	- accountName { String }
	- accountNumber { String }
	- availableBalance { Number }
	- currency { String }
@hideAmounts {Bollean} prop to hide amounts
*/
const props = defineProps({
    classes: String,
    onlyDropdown: Boolean,
    onlyAvailableBalance: Boolean,
	selectedOption: [String, Number],
	options: Array,
    hideAmounts: Boolean
})

const selectedAccount = computed(() => {
	return props.selectedOption ? props.options.find(option => option.id === props.selectedOption) : ''
})

const getAvailableBalanceParams = computed(() => {
    return {
        label: t('selectorWithDetails.accounts.availableBalance'),
        content: amountFormat(Math.abs(selectedAccount.value.availableBalance), selectedAccount.value.currency, null, null, null, props.hideAmounts),
        classes: 'amount-icon'
    }
})
const getAvailableBalanceIcon = computed(() => {
    const availableBalance = selectedAccount.value.availableBalance
    if (availableBalance === 0 || props.hideAmounts) return
    return availableBalance > 0 ? 'plus' : 'minus'
})
const getAvailableBalanceIconColor = computed(() => {
    const availableBalance = selectedAccount.value.availableBalance
    if (availableBalance === 0 || props.hideAmounts) return
    return availableBalance > 0 ? 'positive' : 'negative'
})

const getAccountBalanceParams = computed(() => {
    return {
        label: t('selectorWithDetails.accounts.accountBalance'),
        content: amountFormat(Math.abs(selectedAccount.value.balance), selectedAccount.value.currency, null, null, null, props.hideAmounts),
        classes: 'amount-icon'
    }
})
const getAccountBalanceIcon = computed(() => {
    const balance = selectedAccount.value.balance
    if (balance === 0) return
    return balance > 0 ? 'plus' : 'minus'
})
const getAccountBalanceIconColor = computed(() => {
    const balance = selectedAccount.value.balance
    if (balance === 0) return
    return balance > 0 ? 'positive' : 'negative'
})

/* Web low resolution logic */
const hideBalancesInMoreOptions = ref(false)
const showBalancesFakeDrop = ref(false)

const checkIfHideBalances = () => {
    if (!props.onlyDropdown && !getMobileAppState.value) hideBalancesInMoreOptions.value = window.innerWidth < 1320
}

onMounted(() => {
    checkIfHideBalances()
    window.addEventListener('resize', checkIfHideBalances)
})

onBeforeUnmount(() => {
    window.removeEventListener('resize', checkIfHideBalances)
})
</script>

<style lang="scss" scoped>
.selector-wrapper {
    height: 64px;
    width: 526px;

    display: flex;
    flex-direction: row;

    border-radius: 16px;
    background-color: var(--secondary-light-4);
    box-shadow: 0px 4px 16px 0px rgba(0, 0, 0, 0.08);

    &.border-style {
        box-shadow: unset;
        border: 1px solid var(--secondary-light-1);
    }

    &.only-dropdown {
        width: 254px;
    }

    &.with-hide-balances {
        width: 282px;
    }

    .dropdown-wrapper {
        width: 209px;

        :deep(.dropdown-animation-wrapper) {
            width: 292px;
            left: unset;
            right: -8px;
        }

        &.only-dropdown {
            width: 254px;
        }
    }

    .text-blocks-container {
        display: flex;
        gap: 16px;
        align-items: center;
        flex-grow: 1;
        padding-right: 16px;

        &.inline {
            overflow: hidden;
        }

        .divider {
            height: calc(100% - 28px);
            width: 1px;
            background-color: var(--secondary-light-2);
        }

        .text-blocks-wrapper {
            width: calc(50% + 32px);

            :deep(label) {
                @include H8-Regular;
                line-height: 13px
            }

            :deep(.content) {
                @include H6-Medium;
                color: var(--secondary-dark-2);
            }

            :deep(.content-wrapper) {
                margin-top: 0;
                line-height: 18px;
                gap: 0;

                svg {
                    width: 12px;
                    height: 12px;
                }
            }
        }
    }

    .web-low-resolution-balances-wrapper {
        padding-right: 16px;

        display: flex;
        gap: 16px;
        align-items: center;
        flex-grow: 1;

        position: relative;

        .divider {
            height: calc(100% - 28px);
            width: 1px;
            background-color: var(--secondary-light-2);
        }

        .text-blocks-container {
            padding: 16px;

            position: absolute;
            right: 8px;
            top: 8px;

            display: flex;
            flex-direction: column;

            background-color: var(--secondary-light-4);
            filter: drop-shadow(var(--box-shadow-2));

            .text-blocks-wrapper {
                width: fit-content;
            }
        }
    }

    // MOBILE STYLES
    &.mobile {
        height: 126px;
        width: 100%;
        padding: 0;
        gap: 0;

        position: relative;
        flex-direction: column;

        &.only-dropdown {
            height: 64px;
            width: 100%;

            .dropdown-wrapper {
                border-bottom: unset;
            }
        }

        .dropdown-wrapper,
        .text-blocks-container {
            width: 100%;
            height: 64px;
            align-items: center;
        }

        .dropdown-wrapper {
            justify-content: center;
            border-bottom: 1px solid var(--secondary-light-2);
        }

        .text-blocks-container {
            padding-left: 16px;
            border-left: none;

            .text-blocks-wrapper {
                width: auto;
                height: auto;

                :deep(.content) {
                    white-space: nowrap;
                }

                &:last-of-type {
                    padding-right: 30px;
                }
            }
        }

        .mobile-gradient {
            height: 64px;
            width: 44px;

            position: absolute;
            bottom: 0;
            right: 0;

            border-radius: 16px;
            background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, var(--white) 100%)
        }
    }

    // DROPDOWN ACCOUNTS STYLES
    :deep(.header) {
        border-radius: 16px;
    }
}
</style>
